import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { getEffectiveCalculationType } from './okrCalculationType';
import PhotoEvidenceUpload from '../../components/PhotoEvidenceUpload';

const roundTwo = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return 0;
    return Math.round(number * 100) / 100;
};

const formatNumber = (value) => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? number.toLocaleString('es-PA', { maximumFractionDigits: 2 }) : '0';
};

const getCompositeComponentRatio = (actual, component = {}) => {
    const actualNumber = Number(actual);
    const target = Number(component.target);
    const partialMin = component.partialMin !== undefined && component.partialMin !== null ? Number(component.partialMin) : null;
    if (!Number.isFinite(actualNumber) || !Number.isFinite(target)) return 0;
    if (component.direction && component.direction !== 'higher_is_better') return 0;
    if (actualNumber >= target) return 1;
    if (partialMin !== null && Number.isFinite(partialMin) && target > partialMin && actualNumber >= partialMin) {
        return (actualNumber - partialMin) / (target - partialMin);
    }
    return 0;
};

const getScoringPreview = ({ keyResult, cycle, actualValue, completionPercentage, calculationInput }) => {
    const weight = Number(keyResult?.weight || 0);
    const numberOfWeeks = Number(cycle?.numberOfWeeks || 0);
    const weeklyTarget = Number(keyResult?.weeklyTarget || 0);
    const weeklyBasePoints = numberOfWeeks > 0 ? weight / numberOfWeeks : 0;
    const method = keyResult?.scoringMethod;
    const calculationType = getEffectiveCalculationType(keyResult || {});

    if (!weeklyBasePoints) return null;

    let points = null;

    if (calculationType === 'max_allowed' || method === 'max_allowed') {
        const actual = Number(actualValue);
        if (Number.isFinite(actual) && Number.isFinite(weeklyTarget) && weeklyTarget >= 0) {
            points = actual <= weeklyTarget ? weeklyBasePoints : 0;
        }
    }

    if (calculationType === 'threshold_range' || method === 'threshold_range') {
        const actual = Number(actualValue);
        const thresholds = keyResult?.automationConfig?.thresholds || {};
        const fullPointsAtOrBelow = Number(thresholds.fullPointsAtOrBelow);
        const halfPointsAt = Number(thresholds.halfPointsAt);
        const zeroPointsAtOrAbove = Number(thresholds.zeroPointsAtOrAbove);
        if (
            Number.isFinite(actual) &&
            Number.isFinite(fullPointsAtOrBelow) &&
            Number.isFinite(halfPointsAt) &&
            Number.isFinite(zeroPointsAtOrAbove) &&
            fullPointsAtOrBelow < halfPointsAt &&
            halfPointsAt < zeroPointsAtOrAbove
        ) {
            if (actual <= fullPointsAtOrBelow) points = weeklyBasePoints;
            else if (actual >= zeroPointsAtOrAbove) points = 0;
            else if (actual <= halfPointsAt) points = weeklyBasePoints * (1 - ((actual - fullPointsAtOrBelow) / (halfPointsAt - fullPointsAtOrBelow)) * 0.5);
            else points = weeklyBasePoints * (0.5 - ((actual - halfPointsAt) / (zeroPointsAtOrAbove - halfPointsAt)) * 0.5);
        }
        const thresholdConfig = keyResult?.automationConfig?.thresholdConfig || {};
        const targetValue = Number(thresholdConfig.targetValue || weeklyTarget);
        const partialMinValue = Number(thresholdConfig.partialMinValue);
        if (points === null && Number.isFinite(actual) && Number.isFinite(targetValue) && Number.isFinite(partialMinValue) && targetValue > partialMinValue) {
            if (actual >= targetValue) points = weeklyBasePoints;
            else if (actual < partialMinValue) points = 0;
            else points = ((actual - partialMinValue) / (targetValue - partialMinValue)) * weeklyBasePoints;
        }
    }

    if (calculationType === 'composite_threshold' || method === 'composite_threshold') {
        const components = keyResult?.automationConfig?.compositeConfig?.components || [];
        if (components.length) {
            points = components.reduce((sum, component) => {
                const componentWeight = Number(component.weight);
                if (!Number.isFinite(componentWeight) || componentWeight <= 0) return sum;
                const ratio = getCompositeComponentRatio(calculationInput?.[component.key], component);
                return sum + (weeklyBasePoints * componentWeight * ratio);
            }, 0);
        }
    }

    if (calculationType === 'composite' || method === 'composite') {
        const components = Array.isArray(calculationInput?.components) ? calculationInput.components : [];
        if (components.length) {
            points = components.reduce((sum, component) => {
                const componentWeight = Number(component.weight);
                if (!Number.isFinite(componentWeight) || componentWeight <= 0) return sum;
                const pointsEarned = Number(component.pointsEarned);
                if (Number.isFinite(pointsEarned)) return sum + pointsEarned;
                const ratio = Number(component.ratio);
                return sum + (weeklyBasePoints * componentWeight * Math.max(0, Math.min(1, Number.isFinite(ratio) ? ratio : 0)));
            }, 0);
        }
    }

    if (method === 'higher_is_better') {
        const actual = Number(actualValue);
        if (weeklyTarget > 0 && Number.isFinite(actual)) points = (actual / weeklyTarget) * weeklyBasePoints;
    }

    if (method === 'lower_is_better') {
        const actual = Number(actualValue);
        if (weeklyTarget > 0 && actual > 0) points = (weeklyTarget / actual) * weeklyBasePoints;
    }

    if (method === 'yes_no') {
        const passed = actualValue === true || actualValue === 1 || actualValue === '1' || actualValue === 'yes';
        points = passed ? weeklyBasePoints : 0;
    }

    if (method === 'percentage' || method === 'milestone') {
        const percentage = Number(completionPercentage);
        if (Number.isFinite(percentage)) points = (percentage / 100) * weeklyBasePoints;
    }

    if (points === null || !Number.isFinite(points)) return null;

    return {
        weeklyBasePoints: roundTwo(weeklyBasePoints),
        points: roundTwo(points)
    };
};

const getScoringMethodLabel = (method, calculationType) => {
    if (calculationType === 'max_allowed' || method === 'max_allowed') return 'máximo permitido';
    if (calculationType === 'threshold_range' || method === 'threshold_range') return 'rango con puntos parciales';
    if (calculationType === 'composite_threshold' || method === 'composite_threshold') return 'compuesto por umbrales';
    if (calculationType === 'composite' || method === 'composite') return 'compuesto operativo';
    if (method === 'higher_is_better') return 'mayor es mejor';
    if (method === 'lower_is_better') return 'menor es mejor';
    if (method === 'yes_no') return 'sí / no';
    if (method === 'percentage') return 'porcentaje';
    if (method === 'milestone') return 'hito';
    return method || '-';
};

const getVariance = (forecast, real) => {
    const forecastNumber = Number(forecast);
    const realNumber = Number(real);
    if (!Number.isFinite(forecastNumber) || !Number.isFinite(realNumber) || realNumber === 0) return null;
    return roundTwo((Math.abs(realNumber - forecastNumber) / Math.abs(realNumber)) * 100);
};

const yesNoOptions = [
    { label: 'Sí', value: 1 },
    { label: 'No', value: 0 }
];

const QUICKBOOKS_METRIC_LABELS = {
    coffeeVendingWeeklyRevenue: 'Ventas Coffee & Vending (Máquinas + Café + Contratos)',
    invoicesAmount: 'Facturas',
    netSales: 'Ventas netas',
    creditNotesAmount: 'Notas de crédito',
    collected: 'Cobrado',
    quota: 'Cuota',
    collectionPercentage: '% Cobrado',
    quotaAchievementPercentage: 'Cumplimiento cuota',
    projectedClosing: 'Cierre proyectado',
    keyCustomerRepurchaseDropCount: 'Clientes con caída de recompra ≥20%',
    keyAccountRetentionRate: 'Retención de cuentas bajo Administracion',
    outsideTop10NetSales: 'Ventas netas fuera del Top 10',
    overdueAccountsReceivablePercentage: '% CxC vencida',
    overdueAccountsReceivableAmount: 'CxC vencida',
    totalAccountsReceivableAmount: 'CxC total',
    overdueAccountsPayablePercentage: '% CxP vencida',
    overdueAccountsPayableAmount: 'CxP vencida',
    totalAccountsPayableAmount: 'CxP total'
};

const QUICKBOOKS_UNIT_LABELS = {
    empaque: 'Empaque',
    foods: 'Foods',
    coffee_vending: 'Coffee Vending'
};

const PARLEVEL_METRIC_LABELS = {
    routeSalesAmount: 'Ventas en rutas',
    routeSalesDropCount: 'Rutas con caída de ventas vs semana previa',
    recurringMachineFailureCount: 'Máquinas con falla recurrente',
    routeCompliancePercentage: 'Cumplimiento de rutas %',
    routeComplianceAndParLevel: 'Cumplimiento de ruta + Nivel par'
};

const SYNCRO_OPERATIONS_METRIC_LABELS = {
    machinesReadyForInstallationCount: 'Máquinas habilitadas para instalación',
    machinesInStockForInstallationCount: 'Máquinas de Café y Snacks en inventario',
    openTechnicalCaseCount: 'Casos abiertos técnicos/reclamos',
    averageRepairTimeHours: 'Tiempo promedio de reparación',
    preventiveCompletionCount: 'Cumplimiento de preventivos',
    preventiveCompletionPerRoute: 'Preventivos de campo por ruta',
    ticketsPer10Machines: 'Tickets creados por cada 10 máquinas'
};

const COMPOSITE_METRIC_LABELS = {
    margaritaFailurePreventionAttentionComposite: 'Fallas recurrentes + Preventivos + Coordinación',
    mauricioPreventiveOpenCasesComposite: 'Preventivos completados + Casos abiertos al cierre'
};

const OkrWeeklyEntryDialog = ({
    visible,
    keyResult,
    team,
    objective,
    cycle,
    weekNumber,
    entry,
    saving,
    locked,
    canOverrideLocked,
    onHide,
    onSubmit,
    onSyncFromSource
}) => {
    const [actualValue, setActualValue] = useState(null);
    const [completionPercentage, setCompletionPercentage] = useState(null);
    const [forecast, setForecast] = useState(null);
    const [real, setReal] = useState(null);
    const [notes, setNotes] = useState('');
    const [evidencePhotos, setEvidencePhotos] = useState([]);
    const [error, setError] = useState('');

    const calculationType = getEffectiveCalculationType(keyResult || {});
    const scoringMethod = keyResult?.scoringMethod;
    const isVariance = calculationType === 'variance_forecast_vs_real';
    const isActionCompleted = calculationType === 'action_completed';
    const isMaxAllowed = calculationType === 'max_allowed';
    const variance = useMemo(() => getVariance(forecast, real), [forecast, real]);
    const approvedReadOnly = entry?.status === 'approved' && !canOverrideLocked;
    const editable = (!locked || canOverrideLocked) && !approvedReadOnly;
    const automationConfig = keyResult?.automationConfig || {};
    const sourceType = automationConfig.sourceType || 'manual';
    const isParlevel = sourceType === 'parlevel';
    const isSyncroOperations = sourceType === 'syncro_operations';
    const isComposite = sourceType === 'composite';
    const isFinancialQuickBooks = sourceType === 'quickbooks' && automationConfig.metricCategory === 'financial_report';
    const isCoffeeVendingWeeklyRevenue = sourceType === 'quickbooks' && automationConfig.metricKey === 'coffeeVendingWeeklyRevenue';
    const isReceivableFinancialQuickBooks = isFinancialQuickBooks && automationConfig.metricKey === 'overdueAccountsReceivablePercentage';
    const isTeamTotalQuickBooks = sourceType === 'quickbooks' && automationConfig.aggregationScope === 'team_total';
    const isParlevelFailureMetric = isParlevel && automationConfig.metricKey === 'recurringMachineFailureCount';
    const isParlevelRouteSalesMetric = isParlevel && automationConfig.metricKey === 'routeSalesAmount';
    const isParlevelRouteSalesDropMetric = isParlevel && automationConfig.metricKey === 'routeSalesDropCount';
    const isParlevelRouteComplianceMetric = isParlevel && automationConfig.metricKey === 'routeCompliancePercentage';
    const isParlevelCompositeMetric = isParlevel && automationConfig.metricKey === 'routeComplianceAndParLevel';
    const isOperatorScopedParlevel = ['assigned_machines', 'assigned_routes', 'operator_assigned_machines', 'operator_routes', 'owner_assigned_machines'].includes(String(automationConfig.filters?.routeScope || automationConfig.filters?.scope || automationConfig.routeScope || ''));
    const parlevelScopeLabel = isOperatorScopedParlevel ? 'Máquinas/rutas del operador' : 'Todas las máquinas / rutas';
    const quickBooksUnits = automationConfig.businessUnits?.length ? automationConfig.businessUnits : [automationConfig.businessUnit].filter(Boolean);
    const canSyncFromSource = ['quickbooks', 'parlevel', 'syncro_operations', 'composite'].includes(sourceType) && automationConfig.isEnabled !== false && editable;
    const syncButtonLabels = {
        quickbooks: 'Actualizar desde QuickBooks',
        parlevel: 'Sincronizar Parlevel',
        syncro_operations: 'Sincronizar Syncro',
        composite: 'Sincronizar compuesta'
    };
    const syncButtonLabel = isCoffeeVendingWeeklyRevenue
        ? 'Actualizar ventas'
        : syncButtonLabels[sourceType] || 'Sincronizar dato';
    const previewActualValue = isVariance ? variance : actualValue;
    const displaySourceDetails = useMemo(() => ({
        ...(entry?.calculationInput || {}),
        ...(entry?.sourceDetails || {})
    }), [entry?.calculationInput, entry?.sourceDetails]);
    const displaySourceMetricKey = automationConfig.metricKey || displaySourceDetails?.metricKey || entry?.sourceMetricKey;
    const displaySourceType = automationConfig.sourceType || displaySourceDetails?.sourceType || entry?.sourceType;
    const sourceCalculationInput = useMemo(() => entry?.calculationInput || entry?.sourceDetails || {}, [entry?.calculationInput, entry?.sourceDetails]);
    const preview = getScoringPreview({ keyResult, cycle, actualValue: previewActualValue, completionPercentage, calculationInput: sourceCalculationInput });
    const weeklyBasePoints = preview?.weeklyBasePoints || roundTwo(Number(keyResult?.weight || 0) / Number(cycle?.numberOfWeeks || 0));
    const syncedObtainedValue = entry?.sourceType === 'parlevel' && entry?.sourceMetricKey === 'routeSalesAmount'
        ? entry?.sourceDetails?.routeSalesAmount ?? entry?.sourceDetails?.totalSales ?? entry?.actualValue
        : entry?.actualValue;
    const scoringFormulaText = useMemo(() => {
        const target = Number(keyResult?.weeklyTarget || 0);
        const value = Number(previewActualValue || 0);
        if (!target || !weeklyBasePoints) return null;
        if (calculationType === 'max_allowed' || scoringMethod === 'max_allowed') {
            return `${formatNumber(value)} <= ${formatNumber(target)} ? ${formatNumber(weeklyBasePoints)} pts : 0 pts`;
        }
        if (calculationType === 'threshold_range' || scoringMethod === 'threshold_range') {
            const thresholds = keyResult?.automationConfig?.thresholds || {};
            const fullPointsAtOrBelow = Number(thresholds.fullPointsAtOrBelow);
            const halfPointsAt = Number(thresholds.halfPointsAt);
            const zeroPointsAtOrAbove = Number(thresholds.zeroPointsAtOrAbove);
            if (
                Number.isFinite(fullPointsAtOrBelow) &&
                Number.isFinite(halfPointsAt) &&
                Number.isFinite(zeroPointsAtOrAbove) &&
                fullPointsAtOrBelow < halfPointsAt &&
                halfPointsAt < zeroPointsAtOrAbove
            ) {
                if (value <= fullPointsAtOrBelow) return `${formatNumber(value)} <= ${formatNumber(fullPointsAtOrBelow)} = ${formatNumber(weeklyBasePoints)} pts`;
                if (value >= zeroPointsAtOrAbove) return `${formatNumber(value)} >= ${formatNumber(zeroPointsAtOrAbove)} = 0 pts`;
                if (value <= halfPointsAt) return `Interpolación ${formatNumber(fullPointsAtOrBelow)} a ${formatNumber(halfPointsAt)} tickets / 10 máquinas = ${preview ? formatNumber(preview.points) : 'Pendiente'} pts`;
                return `Interpolación ${formatNumber(halfPointsAt)} a ${formatNumber(zeroPointsAtOrAbove)} tickets / 10 máquinas = ${preview ? formatNumber(preview.points) : 'Pendiente'} pts`;
            }
            const thresholdConfig = keyResult?.automationConfig?.thresholdConfig || {};
            const targetValue = Number(thresholdConfig.targetValue || target);
            const partialMinValue = Number(thresholdConfig.partialMinValue);
            if (!Number.isFinite(partialMinValue) || !Number.isFinite(targetValue) || targetValue <= partialMinValue) return null;
            if (value >= targetValue) return `${formatNumber(value)} >= ${formatNumber(targetValue)} = ${formatNumber(weeklyBasePoints)} pts`;
            if (value < partialMinValue) return `${formatNumber(value)} < ${formatNumber(partialMinValue)} = 0 pts`;
            return `(${formatNumber(value)} - ${formatNumber(partialMinValue)}) / (${formatNumber(targetValue)} - ${formatNumber(partialMinValue)}) x ${formatNumber(weeklyBasePoints)} = ${preview ? formatNumber(preview.points) : 'Pendiente'} pts`;
        }
        if (calculationType === 'composite_threshold' || scoringMethod === 'composite_threshold') {
            const components = keyResult?.automationConfig?.compositeConfig?.components || [];
            if (!components.length) return null;
            return components.map((component) => {
                const actual = sourceCalculationInput?.[component.key];
                const ratio = getCompositeComponentRatio(actual, component);
                const componentPoints = weeklyBasePoints * Number(component.weight || 0) * ratio;
                if (component.partialMin !== undefined && component.partialMin !== null && Number(actual) < Number(component.target) && Number(actual) >= Number(component.partialMin)) {
                    return `${component.label || component.key}: (${formatNumber(actual)} - ${formatNumber(component.partialMin)}) / (${formatNumber(component.target)} - ${formatNumber(component.partialMin)}) x ${formatNumber(weeklyBasePoints * Number(component.weight || 0))} = ${formatNumber(componentPoints)} pts`;
                }
                const unitPrefix = keyResult?.unit === 'USD' ? '$' : '';
                const unitSuffix = keyResult?.unit && keyResult.unit !== 'USD' ? ` ${keyResult.unit}` : '';
                return `${component.label || component.key}: ${unitPrefix}${formatNumber(actual)}${unitSuffix} ${Number(actual) >= Number(component.target) ? '>=' : '<'} ${unitPrefix}${formatNumber(component.target)}${unitSuffix} = ${formatNumber(componentPoints)} pts`;
            }).join(' · ');
        }
        if (calculationType === 'composite' || scoringMethod === 'composite') {
            const components = Array.isArray(sourceCalculationInput?.components) ? sourceCalculationInput.components : [];
            if (!components.length) return null;
            return components
                .map((component) => `${component.label || component.key}: ${formatNumber(component.pointsEarned)} / ${formatNumber(component.maxPoints)} pts`)
                .join(' · ');
        }
        if (scoringMethod === 'higher_is_better') {
            return `${formatNumber(value)} / ${formatNumber(target)} x ${formatNumber(weeklyBasePoints)} = ${preview ? formatNumber(preview.points) : 'Pendiente'} pts`;
        }
        if (scoringMethod === 'lower_is_better') {
            return `${formatNumber(target)} / ${formatNumber(value)} x ${formatNumber(weeklyBasePoints)} = ${preview ? formatNumber(preview.points) : 'Pendiente'} pts`;
        }
        if (scoringMethod === 'percentage' || scoringMethod === 'milestone') {
            return `${formatNumber(completionPercentage)}% x ${formatNumber(weeklyBasePoints)} = ${preview ? formatNumber(preview.points) : 'Pendiente'} pts`;
        }
        return null;
    }, [calculationType, completionPercentage, keyResult?.automationConfig, keyResult?.unit, keyResult?.weeklyTarget, preview, previewActualValue, scoringMethod, sourceCalculationInput, weeklyBasePoints]);

    useEffect(() => {
        if (!visible) return;

        const input = entry?.calculationInput || {};
        setActualValue(entry?.actualValue ?? null);
        setCompletionPercentage(entry?.completionPercentage ?? null);
        setForecast(input.forecast ?? null);
        setReal(input.real ?? null);
        setNotes(entry?.notes || '');
        setEvidencePhotos(Array.isArray(entry?.evidencePhotos) ? entry.evidencePhotos.filter(Boolean) : []);
        setError('');
    }, [entry, visible]);

    const buildPayload = (status) => {
        const payload = {
            keyResultId: keyResult?._id,
            weekNumber,
            status,
            notes,
            evidencePhotos
        };

        if (isVariance) {
            payload.actualValue = variance;
            payload.calculationInput = {
                forecast,
                real,
                variance
            };
            return payload;
        }

        if (isActionCompleted) {
            payload.actualValue = actualValue ? 1 : 0;
            return payload;
        }

        if (isMaxAllowed) {
            payload.actualValue = actualValue;
            return payload;
        }

        if (scoringMethod === 'percentage' || scoringMethod === 'milestone') {
            payload.completionPercentage = completionPercentage;
            payload.actualValue = completionPercentage;
            return payload;
        }

        payload.actualValue = actualValue;
        return payload;
    };

    const validate = () => {
        if (!keyResult?._id || !weekNumber) return 'Selecciona una semana y un resultado clave.';
        if (isVariance && variance === null) return 'Ingresa Forecast y Real para calcular la varianza.';
        if (isMaxAllowed && actualValue === null) return 'Ingresa la cantidad reportada.';
        if (!isVariance && !isActionCompleted && (scoringMethod === 'percentage' || scoringMethod === 'milestone') && completionPercentage === null) {
            return 'Ingresa el porcentaje de avance.';
        }
        if (!isVariance && scoringMethod !== 'percentage' && scoringMethod !== 'milestone' && actualValue === null) {
            return 'Ingresa el valor real reportado.';
        }
        return '';
    };

    const submit = (status) => {
        const validationMessage = validate();
        if (validationMessage) {
            setError(validationMessage);
            return;
        }

        onSubmit(buildPayload(status));
    };

    const footer = (
        <div className="flex justify-content-end gap-2 okr-weekly-entry-actions">
            <Button label="Cancelar" className="p-button-text" onClick={onHide} disabled={saving} />
            {['quickbooks', 'parlevel', 'syncro_operations', 'composite'].includes(sourceType) ? (
                <Button
                    label={syncButtonLabel}
                    icon="pi pi-refresh"
                    className="p-button-outlined"
                    onClick={onSyncFromSource}
                    loading={saving}
                    disabled={!canSyncFromSource}
                />
            ) : null}
            <Button label="Guardar borrador" icon="pi pi-save" className="p-button-outlined" onClick={() => submit('draft')} disabled={saving || !editable} />
            <Button label="Enviar actualización" icon="pi pi-send" onClick={() => submit('submitted')} loading={saving} disabled={!editable} />
        </div>
    );

    return (
        <Dialog
            className="okr-weekly-entry-dialog"
            header="Actualizar semana"
            visible={visible}
            modal
            onHide={onHide}
            footer={footer}
            style={{ width: 'min(720px, 95vw)' }}
            breakpoints={{ '640px': 'calc(100vw - 1rem)' }}
            draggable={false}
        >
            <div className="flex flex-column gap-3 okr-weekly-entry-form">
                {locked ? (
                    <div className="flex align-items-center gap-2 surface-100 border-round p-3">
                        <Tag value="Semana bloqueada" severity="danger" />
                        <span className="text-sm text-color-secondary">
                            {canOverrideLocked ? 'Puedes editar por permisos de administrador.' : 'Esta semana ya no permite cambios.'}
                        </span>
                    </div>
                ) : null}
                {entry?.status === 'approved' ? (
                    <div className="flex align-items-center gap-2 surface-100 border-round p-3">
                        <Tag value="Aprobada" severity="success" />
                        <span className="text-sm text-color-secondary">Esta actualización ya fue aprobada.</span>
                    </div>
                ) : null}
                {entry?.status === 'rejected' && entry?.rejectionReason ? (
                    <div className="surface-100 border-round p-3">
                        <div className="font-medium text-red-600 mb-1">Motivo de rechazo</div>
                        <div className="text-sm text-color-secondary">{entry.rejectionReason}</div>
                    </div>
                ) : null}

                <div className="surface-50 border-round p-3 okr-weekly-entry-summary">
                    <div className="font-semibold mb-1">{keyResult?.title || '-'}</div>
                    <div className="text-sm text-color-secondary">Equipo: {team?.name || '-'} · Objetivo principal: {objective?.title || '-'}</div>
                    <div className="text-sm text-color-secondary">Semana {weekNumber} · Peso {formatNumber(keyResult?.weight)} pts · {formatNumber(weeklyBasePoints)} pts/semana</div>
                    {sourceType === 'quickbooks' ? (
                        <div className="mt-2">
                            <Tag value="Fuente: QuickBooks" severity="info" />
                            {isFinancialQuickBooks ? (
                                <span className="text-sm text-color-secondary ml-2">
                                    Métrica: {QUICKBOOKS_METRIC_LABELS[automationConfig.metricKey] || automationConfig.metricKey || '-'} · Reporte: {isReceivableFinancialQuickBooks ? 'Accounts Receivable Aging' : 'Accounts Payable Aging'} ·{' '}
                                    {isReceivableFinancialQuickBooks ? `Vendedor QuickBooks: ${automationConfig.vendorName || '-'} · ` : ''}
                                    Unidades: {quickBooksUnits.map((unit) => QUICKBOOKS_UNIT_LABELS[unit] || unit).join(' + ')}
                                </span>
                            ) : (
                                <span className="text-sm text-color-secondary ml-2">
                                    Métrica: {QUICKBOOKS_METRIC_LABELS[automationConfig.metricKey] || automationConfig.metricKey || '-'} ·{' '}
                                    Tipo de venta: {isCoffeeVendingWeeklyRevenue ? 'Parlevel semanal + cierre QuickBooks / Devengado' : 'Facturación comercial / contable'} ·{' '}
                                    Alcance: {isTeamTotalQuickBooks ? 'Total del equipo' : 'Vendedor individual'} ·{' '}
                                    {isTeamTotalQuickBooks
                                        ? `Unidades: ${quickBooksUnits.map((unit) => QUICKBOOKS_UNIT_LABELS[unit] || unit).join(' + ')} · Equipo: ${automationConfig.team || '-'}`
                                        : `Vendedor QuickBooks: ${automationConfig.vendorName || '-'} · Unidades: ${quickBooksUnits.map((unit) => QUICKBOOKS_UNIT_LABELS[unit] || unit).join(' + ')}`}
                                </span>
                            )}
                        </div>
                    ) : null}
                    {isParlevel ? (
                        <div className="mt-2">
                            <Tag value="Fuente: Parlevel" severity="info" />
                            <span className="text-sm text-color-secondary ml-2">
                                Métrica: {PARLEVEL_METRIC_LABELS[automationConfig.metricKey] || automationConfig.metricKey || '-'} · Tipo: {isParlevelFailureMetric ? 'Operaciones / fallas' : (isParlevelRouteComplianceMetric || isParlevelCompositeMetric || isParlevelRouteSalesDropMetric) ? 'Operaciones / rutas' : 'Máquinas vending / POS / rutas'} · Alcance: {parlevelScopeLabel}
                                {isParlevelFailureMetric ? ' · Meta permitida: 0 máquinas' : ''}
                                {isParlevelRouteSalesDropMetric ? ` · Umbral de caída: ${formatNumber(automationConfig.filters?.dropThresholdPercentage ?? 15)}% · Comparación: semana previa` : ''}
                                {isParlevelRouteComplianceMetric ? ` · Meta completa: ${formatNumber(automationConfig.thresholdConfig?.targetValue || keyResult?.weeklyTarget || 98)}% · Mínimo parcial: ${formatNumber(automationConfig.thresholdConfig?.partialMinValue || 95)}%` : ''}
                                {isParlevelCompositeMetric ? ' · 50% cumplimiento de ruta · 50% nivel par' : ''}
                                {isParlevelRouteSalesMetric && automationConfig.thresholdMetricKey === 'routeCompliancePercentage'
                                    ? ` · Condición: cumplimiento de rutas ${automationConfig.thresholdOperator || '>='} ${formatNumber(automationConfig.thresholdValue)}%`
                                    : ''}
                            </span>
                        </div>
                    ) : null}
                    {isSyncroOperations ? (
                        <div className="mt-2">
                            <Tag value="Fuente: Syncro Operaciones" severity="info" />
                            <span className="text-sm text-color-secondary ml-2">
                                Métrica: {SYNCRO_OPERATIONS_METRIC_LABELS[automationConfig.metricKey] || automationConfig.metricKey || '-'} ·{' '}
                                {automationConfig.metricKey === 'machinesInStockForInstallationCount'
                                    ? 'Cuenta Café y Snacks En inventario al consultar; no es un inventario histórico'
                                    : automationConfig.metricKey === 'machinesReadyForInstallationCount'
                                    ? 'Cuenta máquinas habilitadas/instaladas durante la semana OKR'
                                    : automationConfig.metricKey === 'openTechnicalCaseCount'
                                        ? 'Cuenta casos técnicos abiertos al cierre de la semana'
                                        : automationConfig.metricKey === 'ticketsPer10Machines'
                                            ? 'Cuenta tickets creados sobre máquinas asignadas a Julio Ortiz'
                                            : 'Promedia horas de reparación de casos cerrados en la semana'}
                            </span>
                        </div>
                    ) : null}
                    {isComposite ? (
                        <div className="mt-2">
                            <Tag value="Fuente: Compuesta" severity="warning" />
                            <span className="text-sm text-color-secondary ml-2">
                                Métrica: {COMPOSITE_METRIC_LABELS[automationConfig.metricKey] || automationConfig.metricKey || '-'} · 33.33% Coordinación · 33.33% Fallas recurrentes · 33.33% Preventivos
                            </span>
                        </div>
                    ) : null}
                </div>

                {entry?.isAutoSynced ? (
                    <div className="surface-100 border-round p-3">
                        <div className="font-medium mb-1">Detalle de sincronización</div>
                        <div className="text-sm text-color-secondary">
                            Fuente: {displaySourceDetails?.sourceSystem || displaySourceType || '-'} · Métrica: {displaySourceDetails?.metricLabel || displaySourceMetricKey || '-'} · Valor obtenido: {displaySourceMetricKey === 'routeComplianceAndParLevel'
                                ? `Cumplimiento ${formatNumber(entry.sourceDetails?.routeCompliancePercentage)}% / Nivel par ${formatNumber(entry.sourceDetails?.avgParLevelPercentage)}%`
                                : formatNumber(syncedObtainedValue)}
                        </div>
                        {entry.sourceDetails?.sourceBusinessMeaning ? (
                            <div className="text-sm text-color-secondary">
                                Origen del dato: {entry.sourceDetails.sourceBusinessMeaning}
                            </div>
                        ) : null}
                        {entry.sourceDetails?.asOfDate ? (
                            <div className="text-sm text-color-secondary">
                                Reporte: {entry.sourceDetails.sourceReport || 'Accounts Payable Aging'} · Fecha de corte: {entry.sourceDetails.asOfDate}
                            </div>
                        ) : entry.sourceDetails?.from && entry.sourceDetails?.to ? (
                            <div className="text-sm text-color-secondary">
                                Rango: {entry.sourceDetails.from} - {entry.sourceDetails.to}
                            </div>
                        ) : null}
                        {entry.sourceDetails?.metricCategory === 'financial_report' ? (
                            <div className="text-sm text-color-secondary mt-1">
                                {entry.sourceDetails?.reportKey === 'accounts_receivable_aging' || entry.sourceDetails?.sourceMetricKey === 'overdueAccountsReceivablePercentage'
                                    ? `CxC vencida: ${formatNumber(entry.sourceDetails.overdueAccountsReceivableAmount)} · CxC total: ${formatNumber(entry.sourceDetails.totalAccountsReceivableAmount)} · % vencido: ${formatNumber(entry.sourceDetails.overdueAccountsReceivablePercentage)}%`
                                    : `CxP vencida: ${formatNumber(entry.sourceDetails.overdueAccountsPayableAmount)} · CxP total: ${formatNumber(entry.sourceDetails.totalAccountsPayableAmount)} · % vencido: ${formatNumber(entry.sourceDetails.overdueAccountsPayablePercentage)}%`}
                            </div>
                        ) : null}
                        {displaySourceType === 'quickbooks' && displaySourceMetricKey === 'netSales' ? (
                            <div className="text-sm text-color-secondary mt-1">
                                Facturas: ${formatNumber(displaySourceDetails?.invoicesAmount)}
                                {' · '}Notas de crédito: -${formatNumber(displaySourceDetails?.creditNotesAmount)}
                                {' · '}Venta neta: ${formatNumber(displaySourceDetails?.netSales ?? syncedObtainedValue)}
                            </div>
                        ) : null}
                        {entry.sourceType === 'quickbooks' && entry.sourceMetricKey === 'coffeeVendingWeeklyRevenue' ? (
                            <div className="text-sm text-color-secondary mt-1">
                                Máquinas (Parlevel {entry.sourceDetails?.machineSalesSource || 'Route Sales'}): ${formatNumber(entry.sourceDetails?.machineSales)}
                                {' · '}Café (cuenta 412{entry.sourceDetails?.coffeeIncludedAtMonthEnd ? ', cierre mensual' : ', no corresponde esta semana'}): ${formatNumber(entry.sourceDetails?.coffeeClosing)}
                                {' · '}Contratos (cuenta 42): ${formatNumber(entry.sourceDetails?.contracts)}
                                {' · '}Total semanal: ${formatNumber(entry.sourceDetails?.totalRevenue ?? entry.actualValue)}
                                {' · '}Base: {entry.sourceDetails?.reportBasis || 'Accrual'}
                            </div>
                        ) : null}
                        {entry.sourceType === 'quickbooks' && entry.sourceMetricKey === 'keyCustomerRepurchaseDropCount' ? (
                            <div className="text-sm text-color-secondary mt-1">
                                Clientes con caída: {formatNumber(entry.sourceDetails?.customerRepurchaseDropCount ?? entry.actualValue)}
                                {' · '}Umbral: {formatNumber(entry.sourceDetails?.dropThresholdPercentage ?? 20)}%
                                {' · '}Comparación: {entry.sourceDetails?.comparisonBasis || 'Semana OKR vs promedio móvil de recompra'}
                                {' · '}Vendedor QuickBooks: {entry.sourceDetails?.vendorName || '-'}
                            </div>
                        ) : null}
                        {entry.sourceType === 'quickbooks' && entry.sourceMetricKey === 'keyAccountRetentionRate' ? (
                            <div className="text-sm text-color-secondary mt-1">
                                Cuentas bajo {entry.sourceDetails?.displayPortfolioName || 'Administracion'}: {formatNumber(entry.sourceDetails?.totalAccounts)}
                                {' · '}Cuentas retenidas: {formatNumber(entry.sourceDetails?.retainedAccounts)}
                                {' · '}Cuentas inactivas: {formatNumber(entry.sourceDetails?.inactiveAccounts)}
                                {' · '}Cuentas en riesgo por caída &gt;=20%: {formatNumber(entry.sourceDetails?.atRiskAccounts)}
                                {' · '}Vendedor QuickBooks: {entry.sourceDetails?.quickbooksVendorNameUsed || 'Administrativo'}
                            </div>
                        ) : null}
                        {displaySourceType === 'quickbooks' && displaySourceMetricKey === 'outsideTop10NetSales' ? (
                            <div className="text-sm text-color-secondary mt-1">
                                Top 10 fijo: {displaySourceDetails?.baselineFrom || '-'} a {displaySourceDetails?.baselineTo || '-'}
                                {' · '}Venta semanal fuera del Top 10: ${formatNumber(displaySourceDetails?.weeklyOutsideTop10Sales ?? entry.actualValue)}
                                {' · '}Meta semanal: ${formatNumber(displaySourceDetails?.weeklyTarget)}
                                {' · '}Acumulado trimestral: ${formatNumber(displaySourceDetails?.quarterToDateOutsideTop10Sales)} / ${formatNumber(displaySourceDetails?.quarterlyTarget)}
                                {' · '}Base julio — Empaques: ${formatNumber(displaySourceDetails?.baselineOutsideTop10SalesByUnit?.empaque)} · Foods: ${formatNumber(displaySourceDetails?.baselineOutsideTop10SalesByUnit?.foods)}
                            </div>
                        ) : null}
                        {entry.sourceType === 'parlevel' ? (
                            <div className="text-sm text-color-secondary mt-1">
                                {entry.sourceDetails?.ownerUserName ? `Operador: ${entry.sourceDetails.ownerUserName} · ` : ''}
                                {entry.sourceMetricKey === 'routeSalesAmount'
                                    ? `Ventas en rutas: ${formatNumber(entry.sourceDetails?.totalSales || entry.actualValue)} · Rutas: ${formatNumber(entry.sourceDetails?.routeCount)} · Máquinas: ${formatNumber(entry.sourceDetails?.machineCount)}`
                                    : entry.sourceMetricKey === 'routeSalesDropCount'
                                        ? `Rutas con caída: ${formatNumber(entry.sourceDetails?.routeSalesDropCount ?? entry.actualValue)} · Umbral: ${formatNumber(entry.sourceDetails?.dropThresholdPercentage ?? 15)}% · Rango actual: ${entry.sourceDetails?.currentWeekRange?.from || entry.sourceDetails?.from || '-'} - ${entry.sourceDetails?.currentWeekRange?.to || entry.sourceDetails?.to || '-'} · Rango comparación: ${entry.sourceDetails?.previousWeekRange?.from || '-'} - ${entry.sourceDetails?.previousWeekRange?.to || '-'} · Fuente ventas: ${entry.sourceDetails?.salesSource || '-'}`
                                    : entry.sourceMetricKey === 'routeCompliancePercentage'
                                        ? `Cumplimiento de rutas: ${formatNumber(entry.sourceDetails?.routeCompliancePercentage ?? entry.actualValue)}% · Avg. Par Level: ${formatNumber(entry.sourceDetails?.avgParLevelPercentage)}% · Rutas activas: ${formatNumber(entry.sourceDetails?.activeRoutes)} · Máquinas: ${formatNumber(entry.sourceDetails?.machineCount)} · Sin atender: ${formatNumber(entry.sourceDetails?.unattendedCount)}`
                                        : entry.sourceMetricKey === 'routeComplianceAndParLevel'
                                            ? `Cumplimiento obtenido: ${formatNumber(entry.sourceDetails?.routeCompliancePercentage)}% · Meta cumplimiento: ${formatNumber(entry.sourceDetails?.routeComplianceTarget || 98)}% · Avg. Par Level obtenido: ${formatNumber(entry.sourceDetails?.avgParLevelPercentage)}% · Meta nivel par: ${formatNumber(entry.sourceDetails?.avgParLevelTarget || 85)}% · Rutas activas: ${formatNumber(entry.sourceDetails?.activeRoutes)} · Máquinas: ${formatNumber(entry.sourceDetails?.machineCount)} · Sin atender: ${formatNumber(entry.sourceDetails?.unattendedCount)}`
                                            : `Máquinas con falla recurrente: ${formatNumber(entry.sourceDetails?.recurrentFailureMachineCount ?? entry.actualValue)} · Umbral: ${formatNumber(entry.sourceDetails?.failureThreshold || 2)} fallas · Meta permitida: 0 máquinas`}
                            </div>
                        ) : null}
                        {entry.sourceType === 'syncro_operations' ? (
                            <div className="text-sm text-color-secondary mt-1">
                                {entry.sourceMetricKey === 'machinesReadyForInstallationCount'
                                    ? `Máquinas habilitadas para instalación: ${formatNumber(entry.sourceDetails?.count ?? entry.actualValue)} · Meta semanal: ${formatNumber(keyResult?.weeklyTarget)} máquinas`
                                    : entry.sourceMetricKey === 'openTechnicalCaseCount'
                                        ? `Fecha de corte: ${entry.sourceDetails?.asOfDate || '-'} · Casos abiertos: ${formatNumber(entry.sourceDetails?.openCaseCount ?? entry.actualValue)} · Meta: menos de ${formatNumber(keyResult?.weeklyTarget)} casos`
                                        : entry.sourceMetricKey === 'preventiveCompletionCount'
                                            ? `Preventivos completados: ${formatNumber(entry.sourceDetails?.preventiveCompletionCount ?? entry.actualValue)} · Meta: ${formatNumber(entry.sourceDetails?.target || keyResult?.weeklyTarget)} · Rutas: ${formatNumber(entry.sourceDetails?.routeCount)}`
                                            : entry.sourceMetricKey === 'preventiveCompletionPerRoute'
                                                ? `Preventivos completados: ${formatNumber(entry.sourceDetails?.completedPreventives)} · Rutas incluidas: ${formatNumber(entry.sourceDetails?.routeCount)} · Promedio por ruta: ${formatNumber(entry.sourceDetails?.averagePerRoute ?? entry.actualValue)} · Meta: >=${formatNumber(entry.sourceDetails?.targetPerRoute || keyResult?.weeklyTarget)} por ruta`
                                                : entry.sourceMetricKey === 'ticketsPer10Machines'
                                                    ? `${formatNumber(entry.sourceDetails?.ticketCount)} tickets / ${formatNumber(entry.sourceDetails?.assignedMachineCount)} máquinas asignadas = ${formatNumber(entry.sourceDetails?.ticketsPer10Machines ?? entry.actualValue)} tickets por cada 10 máquinas · Regla: <=1 = 100% · 2 = 50% · >=3 = 0%`
                                                    : `Tiempo promedio de reparación: ${formatNumber(entry.sourceDetails?.averageRepairTimeHours ?? entry.actualValue)} horas · Meta: <=${formatNumber(keyResult?.weeklyTarget)} horas · Casos cerrados incluidos: ${formatNumber(entry.sourceDetails?.closedCaseCount)}`}
                            </div>
                        ) : null}
                        {entry.sourceType === 'composite' && Array.isArray(entry.sourceDetails?.components) ? (
                            <div className="mt-3 flex flex-column gap-2">
                                <div className="font-medium text-sm">Componentes</div>
                                {entry.sourceDetails.components.map((component) => (
                                    <div key={component.key} className="surface-0 border-round p-2">
                                        <div className="flex justify-content-between gap-2">
                                            <span className="font-medium text-sm">{component.label}</span>
                                            <span className="text-sm">{formatNumber(component.pointsEarned)} / {formatNumber(component.maxPoints)} pts</span>
                                        </div>
                                        {component.key === 'attentionCoordination' ? (
                                            <div className="text-sm text-color-secondary">
                                                Fuente: Parlevel · Cumplimiento ruta: {formatNumber(component.routeCompliancePercentage)}% · Meta: {formatNumber(component.sourceDetails?.routeComplianceTarget || 98)}% · Avg. Par Level: {formatNumber(component.avgParLevelPercentage)}% · Meta nivel par: {formatNumber(component.sourceDetails?.avgParLevelTarget || 85)}%
                                            </div>
                                        ) : component.key === 'recurrentFailureMachines' ? (
                                            <div className="text-sm text-color-secondary">
                                                Fuente: Parlevel · Valor: {formatNumber(component.recurrentFailureMachineCount ?? component.actualValue)} máquinas recurrentes · Meta: 0 · Umbral: {formatNumber(component.failureThreshold || component.sourceDetails?.failureThreshold || 3)}+ fallas por máquina · Eventos: {formatNumber(component.failureEventCount)} · Máquinas con fallas: {formatNumber(component.uniqueMachinesWithFailures)}
                                            </div>
                                        ) : component.key === 'preventiveCompletion' ? (
                                            <div className="text-sm text-color-secondary">
                                                Fuente: {component.sourceDetails?.sourceSystem || 'Syncro Operaciones'} · Preventivos completados: {formatNumber(component.preventiveCompletionCount ?? component.actualValue)} / {formatNumber(component.target || component.sourceDetails?.target || 30)} · Rutas: {formatNumber(component.routeCount || component.sourceDetails?.routeCount)}
                                                {component.sourceDetails?.countsByTaskType ? ` · Tipos: ${Object.entries(component.sourceDetails.countsByTaskType).map(([key, value]) => `${key}: ${formatNumber(value)}`).join(' · ')}` : ''}
                                            </div>
                                        ) : component.key === 'openCasesWeeklyClose' ? (
                                            <div className="text-sm text-color-secondary">
                                                Fuente: Syncro Operaciones · Valor: {formatNumber(component.openCaseCount ?? component.actualValue)} casos abiertos · Meta: menos de {formatNumber(component.target || 75)} · Fecha de corte: {component.asOfDate || component.sourceDetails?.asOfDate || '-'}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-color-secondary">
                                                Fuente: {component.sourceType} · Valor: {formatNumber(component.actualValue)} · Meta: {formatNumber(component.target)}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <div className="text-sm text-color-secondary font-medium">
                                    Total estimado: {formatNumber(entry.sourceDetails.totalPoints)} pts
                                </div>
                            </div>
                        ) : null}
                        {entry.sourceType === 'parlevel' && entry.sourceMetricKey === 'routeCompliancePercentage' ? (
                            <div className="text-sm text-color-secondary mt-1">
                                Meta completa: {formatNumber(entry.sourceDetails?.thresholdConfig?.targetValue || keyResult?.weeklyTarget || 98)}%
                                {' · '}Mínimo parcial: {formatNumber(entry.sourceDetails?.thresholdConfig?.partialMinValue || 95)}%
                            </div>
                        ) : null}
                        {entry.sourceType === 'parlevel' && entry.sourceMetricKey === 'routeSalesAmount' && entry.sourceDetails?.thresholdMetricKey === 'routeCompliancePercentage' ? (
                            <div className="text-sm text-color-secondary mt-1">
                                Cumplimiento de rutas: {formatNumber(entry.sourceDetails.routeCompliancePercentage)}%
                                {' · '}Condición: {entry.sourceDetails.thresholdOperator || '>='} {formatNumber(entry.sourceDetails.thresholdValue)}%
                                {' · '}{entry.sourceDetails.thresholdPassed ? 'Condición cumplida' : 'Condición no cumplida'}
                                {' · '}Valor usado para puntos: {formatNumber(entry.actualValue)}
                            </div>
                        ) : null}
                        {entry.sourceType === 'parlevel' && Array.isArray(entry.sourceDetails?.machines) && entry.sourceDetails.machines.length ? (
                            <div className="text-sm text-color-secondary mt-1">
                                {entry.sourceDetails.machines
                                    .slice(0, 5)
                                    .map((item) => `${item.machineName || 'Máquina sin nombre'}${item.route ? ` (${item.route})` : ''}: ${formatNumber(item.failureCount)} fallas`)
                                    .join(' · ')}
                            </div>
                        ) : null}
                        {entry.sourceType === 'parlevel' && Array.isArray(entry.sourceDetails?.breakdownByRoute) && entry.sourceDetails.breakdownByRoute.length ? (
                            <div className="text-sm text-color-secondary mt-1">
                                {entry.sourceDetails.breakdownByRoute
                                    .slice(0, 5)
                                    .map((item) => `${item.routeName || 'Sin ruta'}: ${formatNumber(item.sales)}`)
                                    .join(' · ')}
                            </div>
                        ) : null}
                        {entry.sourceType === 'parlevel' && Array.isArray(entry.sourceDetails?.routesWithDrop) && entry.sourceDetails.routesWithDrop.length ? (
                            <div className="text-sm text-color-secondary mt-1">
                                {entry.sourceDetails.routesWithDrop
                                    .slice(0, 5)
                                    .map((item) => `${item.routeName || 'Sin ruta'}: ${formatNumber(item.currentWeekSales)} vs ${formatNumber(item.previousWeekSales)} (${formatNumber(item.dropPercentage)}% caída)`)
                                    .join(' · ')}
                            </div>
                        ) : null}
                        {entry.sourceType === 'parlevel' && Array.isArray(entry.sourceDetails?.routeComplianceBreakdownByRoute) && entry.sourceDetails.routeComplianceBreakdownByRoute.length ? (
                            <div className="text-sm text-color-secondary mt-1">
                                {entry.sourceDetails.routeComplianceBreakdownByRoute
                                    .slice(0, 5)
                                    .map((item) => `${item.routeName || 'Sin ruta'}: ${formatNumber(item.compliancePercentage)}%`)
                                    .join(' · ')}
                            </div>
                        ) : null}
                        {entry.sourceType === 'syncro_operations' && Array.isArray(entry.sourceDetails?.machines) && entry.sourceDetails.machines.length ? (
                            <div className="text-sm text-color-secondary mt-1">
                                {entry.sourceDetails.machines
                                    .slice(0, 5)
                                    .map((item) => `${item.machineCode || item.machineName || 'Máquina'}${item.location ? ` (${item.location})` : ''}`)
                                    .join(' · ')}
                            </div>
                        ) : null}
                        {entry.sourceType === 'syncro_operations' && Array.isArray(entry.sourceDetails?.cases) && entry.sourceDetails.cases.length ? (
                            <div className="text-sm text-color-secondary mt-1">
                                {entry.sourceDetails.cases
                                    .slice(0, 5)
                                    .map((item) => `${item.machineCode || item.machineName || 'Caso técnico'}${item.route ? ` (${item.route})` : ''}`)
                                    .join(' · ')}
                            </div>
                        ) : null}
                        {entry.sourceType === 'syncro_operations' && Array.isArray(entry.sourceDetails?.tickets) && entry.sourceDetails.tickets.length ? (
                            <div className="text-sm text-color-secondary mt-1">
                                {entry.sourceDetails.tickets
                                    .slice(0, 5)
                                    .map((item) => `${item.ticketId || 'Ticket'} · ${item.machineCode || item.machineName || 'Máquina'}${item.status ? ` · ${item.status}` : ''}`)
                                    .join(' · ')}
                                {entry.sourceDetails.tickets.length > 5 ? ` · +${entry.sourceDetails.tickets.length - 5} tickets más` : ''}
                            </div>
                        ) : null}
                        {entry.sourceType === 'syncro_operations' && Array.isArray(entry.sourceDetails?.breakdownByRoute) && entry.sourceDetails.breakdownByRoute.length ? (
                            <div className="text-sm text-color-secondary mt-1">
                                {entry.sourceDetails.breakdownByRoute
                                    .slice(0, 6)
                                    .map((route) => `${route.routeName || 'Sin ruta'}: ${formatNumber(route.completedPreventives)} / ${formatNumber(route.target || entry.sourceDetails?.targetPerRoute || 3)}`)
                                    .join(' · ')}
                                {entry.sourceDetails.breakdownByRoute.length > 6 ? ` · +${entry.sourceDetails.breakdownByRoute.length - 6} rutas más` : ''}
                            </div>
                        ) : null}
                        {Array.isArray(displaySourceDetails?.breakdown) && displaySourceDetails.breakdown.length ? (
                            <div className="text-sm text-color-secondary mt-1">
                                {displaySourceDetails.breakdown
                                    .map((item) => displaySourceMetricKey === 'keyCustomerRepurchaseDropCount'
                                        ? `${QUICKBOOKS_UNIT_LABELS[item.businessUnit] || item.businessUnit}: ${formatNumber(item.atRiskCustomerCount)} clientes en riesgo`
                                        : displaySourceMetricKey === 'newCustomerAccountQualification'
                                        ? `${QUICKBOOKS_UNIT_LABELS[item.businessUnit] || item.businessUnit}: ${formatNumber(item.newCustomerCount)} clientes nuevos`
                                        : displaySourceMetricKey === 'outsideTop10NetSales'
                                        ? `${QUICKBOOKS_UNIT_LABELS[item.businessUnit] || item.businessUnit}: $${formatNumber(item.outsideTop10Sales)} / $${formatNumber(item.weeklyTarget)} semanal · $${formatNumber(item.quarterToDateOutsideTop10Sales)} acumulado`
                                        : displaySourceMetricKey === 'netSales'
                                        ? `${QUICKBOOKS_UNIT_LABELS[item.businessUnit] || item.businessUnit}: Facturas $${formatNumber(item.invoicesAmount)} · Notas de crédito -$${formatNumber(item.creditNotesAmount)} · Venta neta $${formatNumber(item.netSales)}`
                                        : displaySourceDetails?.metricCategory === 'financial_report'
                                        ? displaySourceDetails?.reportKey === 'accounts_receivable_aging' || displaySourceDetails?.sourceMetricKey === 'overdueAccountsReceivablePercentage'
                                            ? `${QUICKBOOKS_UNIT_LABELS[item.businessUnit] || item.businessUnit}: CxC vencida ${formatNumber(item.overdueAccountsReceivableAmount)} / total ${formatNumber(item.totalAccountsReceivableAmount)}`
                                            : `${QUICKBOOKS_UNIT_LABELS[item.businessUnit] || item.businessUnit}: CxP vencida ${formatNumber(item.overdueAccountsPayableAmount)} / total ${formatNumber(item.totalAccountsPayableAmount)}`
                                        : `${QUICKBOOKS_UNIT_LABELS[item.businessUnit] || item.businessUnit}: ${formatNumber(item.metricValue)}`)
                                    .join(' · ')}
                            </div>
                        ) : null}
                        {displaySourceType === 'quickbooks' && displaySourceMetricKey === 'newCustomerAccountQualification' ? (
                            <div className="mt-3">
                                <div className="font-medium text-sm mb-2">Clientes nuevos detectados</div>
                                {Array.isArray(displaySourceDetails?.newCustomers) && displaySourceDetails.newCustomers.length ? (
                                    <div className="flex flex-column gap-2">
                                    {displaySourceDetails.newCustomers.slice(0, 10).map((item, index) => (
                                        <div key={`${item.customerName || 'cliente'}-${index}`} className="surface-0 border-round p-2 text-sm">
                                            <span className="font-medium">{item.customerName || 'Cliente sin nombre'}</span>
                                            <span className="text-color-secondary">
                                                {' · '}{Array.isArray(item.businessUnits) && item.businessUnits.length
                                                    ? item.businessUnits.map((unit) => QUICKBOOKS_UNIT_LABELS[unit] || unit).join(' + ')
                                                    : 'Unidad sin identificar'}
                                                {' · '}Venta semanal: ${formatNumber(item.salesAmount)}
                                                {Array.isArray(item.sellers) && item.sellers.length ? ` · Vendedor: ${item.sellers.join(', ')}` : ''}
                                            </span>
                                        </div>
                                    ))}
                                    {displaySourceDetails.newCustomers.length > 10 ? (
                                        <div className="text-xs text-color-secondary">+{displaySourceDetails.newCustomers.length - 10} clientes adicionales</div>
                                    ) : null}
                                    </div>
                                ) : (
                                    <div className="text-sm text-color-secondary">
                                        {Number(entry.actualValue || 0) > 0
                                            ? 'Esta entrada anterior no conserva los nombres. Presiona “Actualizar desde QuickBooks” para recuperar el detalle.'
                                            : 'No se detectaron clientes nuevos con venta comercial en esta semana.'}
                                    </div>
                                )}
                            </div>
                        ) : null}
                        {displaySourceType === 'quickbooks' && displaySourceMetricKey === 'outsideTop10NetSales' ? (
                            <div className="mt-3">
                                <div className="font-medium text-sm mb-2">Clientes fuera del Top 10 que generaron ventas</div>
                                {Array.isArray(displaySourceDetails?.customers) && displaySourceDetails.customers.length ? (
                                    <div className="flex flex-column gap-2">
                                    {displaySourceDetails.customers.slice(0, 20).map((item, index) => (
                                        <div key={`${item.businessUnit || 'unidad'}-${item.customerId || item.customerName || index}`} className="surface-0 border-round p-2 text-sm">
                                            <span className="font-medium">{item.customerName || 'Cliente sin nombre'}</span>
                                            <span className="text-color-secondary">
                                                {' · '}{QUICKBOOKS_UNIT_LABELS[item.businessUnit] || item.businessUnit}
                                                {' · '}Facturas: ${formatNumber(item.invoicesAmount)}
                                                {' · '}Notas de crédito: ${formatNumber(item.creditNotesAmount)}
                                                {' · '}Venta neta: ${formatNumber(item.netSales)}
                                                {Array.isArray(item.sellers) && item.sellers.length ? ` · Vendedor: ${item.sellers.join(', ')}` : ''}
                                            </span>
                                        </div>
                                    ))}
                                    {displaySourceDetails.customers.length > 20 ? (
                                        <div className="text-xs text-color-secondary">+{displaySourceDetails.customers.length - 20} clientes adicionales</div>
                                    ) : null}
                                    </div>
                                ) : (
                                    <div className="text-sm text-color-secondary">
                                        Esta entrada no conserva el desglose por cliente. Presiona “Actualizar desde QuickBooks” para recuperarlo.
                                    </div>
                                )}
                            </div>
                        ) : null}
                        {displaySourceType === 'quickbooks' && displaySourceMetricKey === 'outsideTop10NetSales' && displaySourceDetails?.topCustomersByUnit ? (
                            <div className="text-xs text-color-secondary mt-2">
                                Top 10 excluido · {Object.entries(displaySourceDetails.topCustomersByUnit)
                                    .map(([unit, customers]) => `${QUICKBOOKS_UNIT_LABELS[unit] || unit}: ${(customers || []).map((customer) => customer.customerName).join(', ') || 'Sin datos'}`)
                                    .join(' · ')}
                            </div>
                        ) : null}
                        {entry.sourceType === 'quickbooks' && Array.isArray(entry.sourceDetails?.customersWithDrop) && entry.sourceDetails.customersWithDrop.length ? (
                            <div className="text-xs text-color-secondary mt-2">
                                {entry.sourceDetails.customersWithDrop
                                    .slice(0, 5)
                                    .map((item) => `${item.customer}: ${formatNumber(item.dropPercent)}% caída`)
                                    .join(' · ')}
                                {entry.sourceDetails.customersWithDrop.length > 5 ? ` · +${entry.sourceDetails.customersWithDrop.length - 5} más` : ''}
                            </div>
                        ) : null}
                        {entry.sourceType === 'quickbooks' && Array.isArray(entry.sourceDetails?.customerDetails) && entry.sourceDetails.customerDetails.length ? (
                            <div className="text-xs text-color-secondary mt-2">
                                {entry.sourceDetails.customerDetails
                                    .slice(0, 6)
                                    .map((item) => `${item.customerName}: ${formatNumber(item.currentPeriodSales)} vs ${formatNumber(item.baselineMonthlyAverage ?? item.baselineSales)} (${formatNumber(item.dropPercentage)}% variación, ${item.status || (item.retained ? 'Retenida' : 'Inactiva')})`)
                                    .join(' · ')}
                                {entry.sourceDetails.customerDetails.length > 6 ? ` · +${entry.sourceDetails.customerDetails.length - 6} clientes más` : ''}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {['quickbooks', 'parlevel', 'syncro_operations', 'composite'].includes(sourceType) ? (
                    <div className="surface-50 border-round p-3">
                        <div className="font-medium mb-2">Cómo se calculan los puntos</div>
                        <div className="text-sm text-color-secondary">
                            Meta semanal: {keyResult?.unit === 'USD' ? '$' : ''}{formatNumber(keyResult?.weeklyTarget)}{keyResult?.unit && keyResult.unit !== 'USD' ? ` ${keyResult.unit}` : ''}
                            {' · '}Valor obtenido: {entry?.sourceMetricKey === 'routeComplianceAndParLevel'
                                ? `Cumplimiento ${formatNumber(entry.sourceDetails?.routeCompliancePercentage)}% / Nivel par ${formatNumber(entry.sourceDetails?.avgParLevelPercentage)}%`
                                : entry?.sourceType === 'composite'
                                    ? 'Ver componentes'
                                : formatNumber(entry?.isAutoSynced ? syncedObtainedValue : previewActualValue)}
                            {' · '}Valor usado para puntos: {formatNumber(previewActualValue)}
                        </div>
                        <div className="text-sm text-color-secondary">
                            Puntos/semana: {formatNumber(weeklyBasePoints)} · Método: {getScoringMethodLabel(scoringMethod, calculationType)}
                        </div>
                        {entry?.sourceType === 'parlevel' && entry?.sourceMetricKey === 'routeSalesAmount' && entry?.sourceDetails?.thresholdMetricKey === 'routeCompliancePercentage' ? (
                            <div className="text-sm text-color-secondary">
                                Condición: cumplimiento de rutas {entry.sourceDetails.thresholdOperator || '>='} {formatNumber(entry.sourceDetails.thresholdValue)}% · Resultado: {formatNumber(entry.sourceDetails.routeCompliancePercentage)}% · {entry.sourceDetails.thresholdPassed ? 'Cumplida' : 'No cumplida'}
                            </div>
                        ) : null}
                        {scoringFormulaText ? (
                            <div className="text-sm text-color-secondary">
                                Cálculo: {scoringFormulaText}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {keyResult?.description ? (
                    <div>
                        <label className="block font-medium mb-2">Criterio de medición</label>
                        <div className="text-sm text-color-secondary">{keyResult.description}</div>
                    </div>
                ) : null}

                {isVariance ? (
                    <div className="grid">
                        <div className="col-12 md:col-4">
                            <label className="block font-medium mb-2">Forecast</label>
                            <InputNumber
                                value={forecast}
                                onValueChange={(event) => setForecast(event.value)}
                                mode={keyResult?.unit === 'USD' ? 'currency' : 'decimal'}
                                currency={keyResult?.unit === 'USD' ? 'USD' : undefined}
                                locale="en-US"
                                minFractionDigits={0}
                                maxFractionDigits={2}
                                useGrouping
                                className="w-full"
                                disabled={!editable}
                            />
                        </div>
                        <div className="col-12 md:col-4">
                            <label className="block font-medium mb-2">Real</label>
                            <InputNumber
                                value={real}
                                onValueChange={(event) => setReal(event.value)}
                                mode={keyResult?.unit === 'USD' ? 'currency' : 'decimal'}
                                currency={keyResult?.unit === 'USD' ? 'USD' : undefined}
                                locale="en-US"
                                minFractionDigits={0}
                                maxFractionDigits={2}
                                useGrouping
                                className="w-full"
                                disabled={!editable}
                            />
                        </div>
                        <div className="col-12 md:col-4">
                            <label className="block font-medium mb-2">Varianza calculada %</label>
                            <InputText value={variance === null ? 'Pendiente' : `${formatNumber(variance)}%`} className="w-full" readOnly />
                        </div>
                    </div>
                ) : (
                    <div className="grid">
                        {isActionCompleted ? (
                            <div className="col-12 md:col-6">
                                <label className="block font-medium mb-2">Acción completada</label>
                                <Dropdown value={actualValue} options={yesNoOptions} onChange={(event) => setActualValue(event.value)} className="w-full" disabled={!editable} />
                                <small className="text-color-secondary block mt-1">
                                    Si marcas Sí, gana los puntos semanales completos. Si marcas No, gana 0.
                                </small>
                            </div>
                        ) : isMaxAllowed ? (
                            <div className="col-12 md:col-6">
                                <label className="block font-medium mb-2">Cantidad reportada</label>
                                <InputNumber
                                    value={actualValue}
                                    onValueChange={(event) => setActualValue(event.value)}
                                    min={0}
                                    locale="en-US"
                                    minFractionDigits={0}
                                    maxFractionDigits={2}
                                    useGrouping
                                    className="w-full"
                                    disabled={!editable}
                                />
                                <small className="text-color-secondary block mt-1">
                                    Ejemplo: si la meta es 0 quejas, ingresa 0 si no hubo quejas o el número real si hubo.
                                </small>
                            </div>
                        ) : scoringMethod === 'yes_no' ? (
                            <div className="col-12 md:col-6">
                                <label className="block font-medium mb-2">Resultado Sí / No</label>
                                <Dropdown value={actualValue} options={yesNoOptions} onChange={(event) => setActualValue(event.value)} className="w-full" disabled={!editable} />
                            </div>
                        ) : scoringMethod === 'percentage' || scoringMethod === 'milestone' ? (
                            <div className="col-12 md:col-6">
                                <label className="block font-medium mb-2">Porcentaje de avance</label>
                                <InputNumber
                                    value={completionPercentage}
                                    onValueChange={(event) => setCompletionPercentage(event.value)}
                                    suffix="%"
                                    min={0}
                                    max={100}
                                    minFractionDigits={0}
                                    maxFractionDigits={2}
                                    useGrouping
                                    className="w-full"
                                    disabled={!editable}
                                />
                            </div>
                        ) : (
                            <div className="col-12 md:col-6">
                                <label className="block font-medium mb-2">Valor real reportado</label>
                                <InputNumber
                                    value={actualValue}
                                    onValueChange={(event) => setActualValue(event.value)}
                                    mode={keyResult?.unit === 'USD' ? 'currency' : 'decimal'}
                                    currency={keyResult?.unit === 'USD' ? 'USD' : undefined}
                                    locale="en-US"
                                    minFractionDigits={0}
                                    maxFractionDigits={2}
                                    useGrouping
                                    className="w-full"
                                    disabled={!editable}
                                />
                            </div>
                        )}
                    </div>
                )}

                <div>
                    <label className="block font-medium mb-2">Notas</label>
                    <InputTextarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} className="w-full" disabled={!editable} />
                </div>

                <PhotoEvidenceUpload
                    label="Foto de evidencia"
                    value={evidencePhotos}
                    onChange={setEvidencePhotos}
                    disabled={!editable}
                    readOnly={!editable}
                    maxPhotos={10}
                    maxSizeMB={1.5}
                    helperText="10 fotos. Máximo 1.5 MB cada una."
                />

                <div className="surface-50 border-round p-3 okr-points-preview">
                    <div className="flex align-items-center justify-content-between gap-3">
                        <div>
                            <div className="font-semibold">Puntos estimados</div>
                            <div className="text-sm text-color-secondary">Los puntos finales se calculan al guardar.</div>
                        </div>
                        <div className="text-xl font-bold">{preview ? `${formatNumber(preview.points)} pts` : 'Pendiente'}</div>
                    </div>
                </div>

                {entry?.status ? <Tag value={`Estado actual: ${entry.status}`} severity="info" className="align-self-start" /> : null}
                {error ? <small className="p-error">{error}</small> : null}
            </div>
        </Dialog>
    );
};

export default OkrWeeklyEntryDialog;
